import type { FC, ReactNode } from 'react'
import type { MessageTemplateKey, PaymentMethod } from '@/types'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import {
    BadgePercent,
    CircleCheckBig,
    Clock3,
    MessageCircle,
    UserRound
} from 'lucide-react'
import { Avatar } from '@/components/kit/avatar'
import { Plate } from '@/components/kit/plate'
import { Sheet } from '@/components/kit/sheet'
import { useToast } from '@/components/kit/toast'
import { Button, buttonClasses } from '@/components/kit/button'
import { JobStatusBadge } from '@/components/kit/badge'
import { JobMinutes, JobProgress } from '@/components/jobs/job-timing'
import { DiscountEditor } from '@/components/billing/discount-editor'
import { PaymentMethodPicker } from '@/components/billing/payment-method-picker'
import { useCarWash } from '@/hooks/use-carwash'
import { useCarWashActions } from '@/hooks/use-carwash-actions'
import { PAYMENT_METHOD_LABEL } from '@/constants/carwash'
import { MINUTE, formatMinutes, formatMoney, formatTime } from '@/lib/carwash/format'
import { getElapsedMs } from '@/lib/carwash/selectors'
import { plateLabel } from '@/lib/carwash/plate'

export type CheckoutTarget =
    | { kind: 'job', jobId: string }
    | { kind: 'invoice', invoiceId: string }

interface CheckoutSheetProps {
    target: CheckoutTarget | null
    open: boolean
    onClose: () => void
    onWhatsApp: (input: { jobId: string | null, invoiceId: string | null, template: MessageTemplateKey }) => void
}

type Step = 'confirm' | 'payment' | 'paid'

/*
 * Completing a wash and collecting payment in one place: confirm the wash
 * is finished, then the bill appears straight away with the price of the
 * chosen service, ready to be marked paid or left pending.
 */
export const CheckoutSheet: FC<CheckoutSheetProps> = ({
    target,
    open,
    onClose,
    onWhatsApp
}): ReactNode => {
    const { state, lookups } = useCarWash()
    const actions = useCarWashActions()
    const toast = useToast()

    const [step, setStep] = useState<Step>('confirm')
    const [invoiceId, setInvoiceId] = useState<string | null>(null)
    const [justCompleted, setJustCompleted] = useState(false)
    const [method, setMethod] = useState<PaymentMethod>('cash')
    const [showDiscount, setShowDiscount] = useState(false)

    useEffect(() => {
        if (!open || !target || !lookups || !state) return

        setShowDiscount(false)
        setJustCompleted(false)
        setMethod(state.settings.defaultPaymentMethod)

        if (target.kind === 'invoice') {
            const invoice = lookups.invoices.get(target.invoiceId)
            setInvoiceId(target.invoiceId)
            setStep(invoice?.status === 'paid' ? 'paid' : 'payment')
            return
        }

        const job = lookups.jobs.get(target.jobId)

        if (job?.invoiceId) {
            const invoice = lookups.invoices.get(job.invoiceId)
            setInvoiceId(job.invoiceId)
            setStep(invoice?.status === 'paid' ? 'paid' : 'payment')
        }
        else {
            setInvoiceId(null)
            setStep('confirm')
        }
        // Reset only when the sheet opens for a new target.
    }, [open, target]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!state || !lookups) return null

    const currency = state.settings.currency
    const invoice = invoiceId ? lookups.invoices.get(invoiceId) ?? null : null
    const job = target?.kind === 'job'
        ? lookups.jobs.get(target.jobId) ?? null
        : invoice ? lookups.jobs.get(invoice.jobId) ?? null : null
    const staff = job?.staffId ? lookups.staff.get(job.staffId) ?? null : null
    const plate = job ? plateLabel(job.plate, `Vehicle #${job.number}`) : invoice ? plateLabel(invoice.plate) : ''

    const complete = () => {
        if (!job) return

        const created = actions.completeWash(job.id)
        if (!created) return

        setInvoiceId(created)
        setJustCompleted(true)
        setStep('payment')
    }

    const markPaid = () => {
        if (!invoice) return
        if (!actions.markPaid(invoice.id, method)) return

        setStep('paid')
    }

    const collectLater = () => {
        if (invoice) toast({ title: `Bill ${invoice.number} saved as pending`, description: 'You can collect it any time from Billing.', tone: 'info' })
        onClose()
    }

    const whatsapp = (template: MessageTemplateKey) => onWhatsApp({ jobId: job?.id ?? null, invoiceId: invoice?.id ?? null, template })

    let title = 'Complete wash'
    let description: string | undefined = plate ? `${plate}${job ? `, ${job.serviceName}` : ''}` : undefined
    let footer: ReactNode = null
    let body: ReactNode = null

    if (step === 'confirm' && job) {
        const finishable = job.status === 'washing' || job.status === 'quality_check'

        footer = (
            <Button size='xl' className='w-full' disabled={!finishable} onClick={complete}>
                <CircleCheckBig aria-hidden='true' />
                {`Complete wash and bill ${formatMoney(job.price, currency)}`}
            </Button>
        )

        body = (
            <div className='space-y-4'>
                <div className='flex items-center justify-between gap-3'>
                    <div className='min-w-0'>
                        <Plate value={job.plate} size='lg' />
                        <p className='mt-2 truncate text-sm text-muted-foreground'>
                            {job.vehicleModel ?? 'Model not added'}
                        </p>
                    </div>

                    <JobStatusBadge status={job.status} />
                </div>

                <dl className='divide-y divide-border rounded-xl border border-border'>
                    <div className='flex items-center justify-between gap-3 px-4 py-3'>
                        <dt className='text-sm text-muted-foreground'>
                            Service
                        </dt>
                        <dd className='text-sm font-semibold text-foreground'>
                            {`${job.serviceName}, ${formatMoney(job.price, currency)}`}
                        </dd>
                    </div>

                    <div className='flex items-center justify-between gap-3 px-4 py-3'>
                        <dt className='flex items-center gap-2 text-sm text-muted-foreground'>
                            <UserRound className='size-4' aria-hidden='true' />
                            Staff
                        </dt>
                        <dd className='flex items-center gap-2 text-sm font-semibold text-foreground'>
                            {staff && <Avatar name={staff.name} photo={staff.photo} size='xs' />}
                            {staff?.name ?? 'Not assigned'}
                        </dd>
                    </div>

                    <div className='flex items-center justify-between gap-3 px-4 py-3'>
                        <dt className='flex items-center gap-2 text-sm text-muted-foreground'>
                            <Clock3 className='size-4' aria-hidden='true' />
                            {job.startedAt ? `Started ${formatTime(job.startedAt)}` : 'Not started'}
                        </dt>
                        <dd className='text-sm font-semibold text-foreground'>
                            <JobMinutes job={job} />
                        </dd>
                    </div>
                </dl>

                <JobProgress job={job} />

                {!finishable && (
                    <p className='rounded-lg bg-amber-50 px-3 py-2 text-[13px] font-medium text-amber-900'>
                        Start the wash before completing it.
                    </p>
                )}
            </div>
        )
    }

    if (step === 'payment' && invoice) {
        title = 'Collect payment'
        description = `${invoice.number}, ${plate}`

        footer = (
            <div className='grid grid-cols-[auto_1fr] gap-3'>
                <Button variant='secondary' size='xl' onClick={collectLater}>
                    Pay later
                </Button>

                <Button variant='success' size='xl' onClick={markPaid}>
                    {`Mark paid ${formatMoney(invoice.total, currency)}`}
                </Button>
            </div>
        )

        body = (
            <div className='space-y-5'>
                {justCompleted && job && (
                    <div className='flex items-start gap-3 rounded-xl bg-emerald-50 px-4 py-3 text-emerald-900'>
                        <CircleCheckBig className='mt-0.5 size-5 shrink-0 text-emerald-600' aria-hidden='true' />
                        <p className='text-sm'>
                            <span className='font-semibold'>Wash completed</span>
                            {` in ${formatMinutes(getElapsedMs(job, Date.now()) / MINUTE)}.`}
                            {staff ? ` ${staff.name} is free for the next vehicle.` : ''}
                        </p>
                    </div>
                )}

                <div className='rounded-2xl border border-border bg-surface p-4'>
                    <p className='text-[13px] font-semibold text-muted-foreground'>
                        Amount due
                    </p>

                    <p className='mt-1 font-heading text-4xl font-extrabold text-foreground'>
                        {formatMoney(invoice.total, currency)}
                    </p>

                    <dl className='mt-3 space-y-1.5 border-t border-border pt-3 text-sm'>
                        {invoice.lines.map((line, index) => (
                            <div key={index} className='flex justify-between gap-3'>
                                <dt className='text-muted-foreground'>
                                    {line.label}
                                </dt>
                                <dd className='font-medium text-foreground tabular-nums'>
                                    {formatMoney(line.amount, currency)}
                                </dd>
                            </div>
                        ))}

                        {invoice.discountAmount > 0 && (
                            <div className='flex justify-between gap-3 text-emerald-700'>
                                <dt>
                                    {invoice.discountType === 'percent' ? `Discount (${invoice.discountValue}%)` : 'Discount'}
                                </dt>
                                <dd className='font-medium tabular-nums'>
                                    {`-${formatMoney(invoice.discountAmount, currency)}`}
                                </dd>
                            </div>
                        )}
                    </dl>

                    {!showDiscount && (
                        <button
                            type='button'
                            onClick={() => setShowDiscount(true)}
                            className='mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline'
                        >
                            <BadgePercent className='size-4' aria-hidden='true' />
                            {invoice.discountAmount > 0 ? 'Change discount' : 'Add discount'}
                        </button>
                    )}
                </div>

                {showDiscount && (
                    <DiscountEditor invoice={invoice} currency={currency} onDone={() => setShowDiscount(false)} />
                )}

                <div>
                    <p className='mb-2 text-sm font-semibold text-foreground'>
                        Payment method
                    </p>

                    <PaymentMethodPicker value={method} onChange={setMethod} />
                </div>

                <button
                    type='button'
                    onClick={() => whatsapp('completed')}
                    className='flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                >
                    <span className='grid size-9 place-items-center rounded-lg bg-emerald-50 text-emerald-700'>
                        <MessageCircle className='size-5' aria-hidden='true' />
                    </span>

                    <span className='min-w-0 flex-1'>
                        <span className='block text-sm font-semibold text-foreground'>
                            Tell the customer it is ready
                        </span>
                        <span className='block text-[13px] text-muted-foreground'>
                            Opens a WhatsApp message you can edit
                        </span>
                    </span>
                </button>
            </div>
        )
    }

    if (step === 'paid' && invoice) {
        title = 'Payment received'
        description = `${invoice.number}, ${plate}`

        footer = (
            <Button size='xl' className='w-full' onClick={onClose}>
                Done
            </Button>
        )

        body = (
            <div className='space-y-5'>
                <div className='flex flex-col items-center rounded-2xl bg-emerald-50 px-4 py-6 text-center'>
                    <span className='grid size-14 place-items-center rounded-full bg-emerald-600 text-white animate-in zoom-in-50 duration-300'>
                        <CircleCheckBig className='size-7' aria-hidden='true' />
                    </span>

                    <p className='mt-3 font-heading text-3xl font-extrabold text-emerald-900'>
                        {formatMoney(invoice.total, currency)}
                    </p>

                    <p className='mt-1 text-sm font-medium text-emerald-800'>
                        {`Paid by ${invoice.paymentMethod ? PAYMENT_METHOD_LABEL[invoice.paymentMethod] : 'customer'}${invoice.paidAt ? ` at ${formatTime(invoice.paidAt)}` : ''}`}
                    </p>
                </div>

                <div className='grid gap-3 sm:grid-cols-2'>
                    <Button variant='secondary' size='lg' onClick={() => whatsapp('payment')}>
                        <MessageCircle aria-hidden='true' />
                        Send thank-you
                    </Button>

                    <Link href={`/billing/${invoice.id}`} onClick={onClose} className={buttonClasses({ variant: 'secondary', size: 'lg' })}>
                        View bill
                    </Link>
                </div>
            </div>
        )
    }

    return (
        <Sheet
            open={open && Boolean(body)}
            onClose={onClose}
            title={title}
            description={description}
            footer={footer}
        >
            {body}
        </Sheet>
    )
}
